import {
	defineStore
} from "pinia";
import {
	ref,
	computed,
	inject
} from "vue";
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import {
	Local,
	Session,
	STORAGE_KEYS
} from "@/utils/storage";

import {
	Crisp
} from 'crisp-sdk-web'
import {
	getUserInfo,
	login as _login,
	loginByMobile as _loginByMobile,
	refreshTokenApi,
	logout as _logout
} from '@/api/login/index';
import Cookies from "js-cookie";
import {
	isFingerprintBrowser
} from "@/utils/isFingerprintBrowser";
export const useUserStore = defineStore("user", () => {
	// 状态
	const userInfo = ref({
		user: {
			username: '',
			userId: ''
		},
		userName: '',
		photo: '',
		time: 0,
		roles: [],
		authBtnList: [],
		tenantId: '',
		tenantName: '',
		// 代理信息
		principalId: null,
		inviteCode: '',
	});
	// 浏览器指纹
	const browserId = ref(null);
	// 邀请码
	const invitationCode = ref(null);
	// 渠道推广码
	const channelCode = ref(null);
	// 完整推广链接（去掉协议）
	const promotionDomain = ref(null);
	// 响应式 token（用于 SSE 等组件监听 token 变化）
	const accessToken = ref(Session.getToken() || null);

	// 游戏结算中状态（余额区域显示 loading）
	const settling = ref(false);
	// 当前是否在游戏承载页里，SSE 弹窗和右侧浮窗都按它控制展示时机
	const inGame = ref(false);

	// 计算属性

	// 方法
	function setUserInfo(info) {
		userInfo.value = info;
	}

	function clearPopupDismissed() {
		try {
			window.sessionStorage.removeItem('popup_float_dismissed_items');
			window.localStorage.removeItem('popup_float_dismissed_items');
			window.dispatchEvent(new Event('popup:reset'));
		} catch {}
	}

	/**
	 * 登录方法
	 * @function login
	 * @async
	 * @param {Object} data - 登录数据 {username, password, code, randomStr}
	 * @returns {Promise<Object>}
	 */
	async function login(data) {
		data.grant_type = 'password';
		data.scope = 'server';
		data.flag = isFingerprintBrowser();
		return new Promise((resolve, reject) => {
			_login(data)
				.then((res) => {
					// 存储token 信息
					Session.set(STORAGE_KEYS.TOKEN, res?.access_token);
					Session.set(STORAGE_KEYS.REFRESH_TOKEN, res?.refresh_token);
					// 更新响应式 token
					accessToken.value = res?.access_token;
					clearPopupDismissed();
					//获取用户信息
					setUserInfos();
					
					resolve(res);
				})
				.catch((err) => {
					// useMessage().error(err?.msg || '系统异常请联系管理员');
					reject(err);
				});
		});
	}

	/**
	 * 手机登录方法
	 * @function loginByMobile
	 * @async
	 * @param {Object} data - 登录数据
	 * @returns {Promise<Object>}
	 */
	async function loginByMobile(data) {
		return new Promise((resolve, reject) => {
			_loginByMobile(data.mobile, data.code)
				.then((res) => {
					// 存储token 信息
					Session.set(STORAGE_KEYS.TOKEN, res?.access_token);
					Session.set(STORAGE_KEYS.REFRESH_TOKEN, res?.refresh_token);
					// 更新响应式 token
					accessToken.value = res?.access_token;
					clearPopupDismissed();
					
					resolve(res);
				})
				.catch((err) => {
					// useMessage().error(err?.msg || '系统异常请联系管理员');
					reject(err);
				});
		});
	}

	/**
	 * 获取用户信息方法
	 * @function setUserInfos
	 * @async
	 */
	async function setUserInfos() {
		await getUserInfo().then((res) => {
			userInfo.value = {
				user: res?.data?.appUser,
				roles: res?.data?.roles,
				authBtnList: res?.data?.permissions,
				// 代理信息
				principalId: res?.data?.principalId,
				inviteCode: res?.data?.inviteCode,
				// 钱包列表
				wallets: res?.data?.wallets || [],
				// 提现密码状态
				hasWithdrawPassword: res?.data?.hasWithdrawPassword || false,
				// VIP 等级与升级进度
				vipLevel: res?.data?.vipLevel ?? 0,
				vipProgress: res?.data?.vipProgress || null,

				loginInfo: res?.data?.loginInfo || [],
			};
		});
	}

	/**
	 * 刷新token方法
	 * @function refreshToken
	 * @async
	 * @returns {Promise<any>}
	 */
	async function refreshToken() {
		return new Promise((resolve, reject) => {
			const refreshToken = Session.get(STORAGE_KEYS.REFRESH_TOKEN);
			refreshTokenApi(refreshToken)
				.then((res) => {
					// 存储token 信息
					Session.set(STORAGE_KEYS.TOKEN, res?.access_token);
					Session.set(STORAGE_KEYS.REFRESH_TOKEN, res?.refresh_token);
					// 更新响应式 token
					accessToken.value = res?.access_token;
					resolve(res);
				})
				.catch((err) => {
					// useMessage().error(err.msg);
					reject(err);
				});
		});
	}

	/**
	 * 更新租户信息方法
	 * @function updateTenantInfo
	 * @param {string} tenantId - 租户ID
	 * @param {string} tenantName - 租户名称
	 */
	function updateTenantInfo(tenantId, tenantName) {
		if (userInfo.value) {
			userInfo.value.tenantId = tenantId;
			userInfo.value.tenantName = tenantName;
		}

		// 保存租户信息到本地
		Session.set(STORAGE_KEYS.TENANT_ID, tenantId);
		Local.set(STORAGE_KEYS.TENANT_ID, tenantId);
		Cookies.set(STORAGE_KEYS.TENANT_ID, tenantId);
	}

	async function logout() {
		// 调用后台接口
		await _logout();
		
		userInfo.value = null;
		accessToken.value = null;
		// 清除缓存/token等
		Session.clear();
		// 清除 Pinia 持久化的 user-store
		localStorage.removeItem('user-store');
		Crisp.setTokenId();
		Crisp.session.reset();
		Crisp.chat.hide();
	}

	// 获取指纹ID
	async function getBrowserId() {
		const fp = await FingerprintJS.load()
		const result = await fp.get()
		browserId.value = result.visitorId
	}

	return {
		// 状态
		userInfo,
		browserId,
		invitationCode,
		channelCode,
		promotionDomain,
		accessToken,
		settling,
		inGame,
		// 计算属性
		// 方法
		setUserInfo,
		logout,
		getBrowserId,
		loginByMobile,
		login,
		updateTenantInfo,
		setUserInfos,
		refreshToken
	};
}, {
	persist: {
		key: "user-store",
		storage: localStorage,
		paths: ["userInfo", "browserId", "invitationCode", "channelCode", "promotionDomain"],
	},
});